import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Button — UI Foundation Phase 1 (contract: /docs/design.md §Buttons, A5, A11).
 *
 * Geometry (height, radius) comes from CSS variables, not from literal classes:
 * that is what lets the Admin Console keep its current, denser look inside
 * `.legacy-admin-scope` while the user-facing app adopts the 44px pill.
 *
 * Variants: default (primary) · secondary · outline · ghost · danger · link.
 * `size="dense"` is the A11 product variant for toolbars and table rows — it is
 * opt-in, never the default.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-button)] text-sm font-medium " +
  "transition-[background-color,color,border-color,box-shadow,transform] duration-base ease-system " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas " +
  "disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-brand-hover hover:-translate-y-px",
        destructive:
          "bg-danger-solid text-white shadow-sm hover:bg-danger-solid/90",
        danger:
          "bg-danger-solid text-white shadow-sm hover:bg-danger-solid/90",
        outline:
          "border border-line bg-transparent text-content hover:bg-surface-subtle hover:border-line-strong",
        secondary:
          "bg-secondary text-secondary-foreground border border-line-soft hover:bg-surface-sunken",
        ghost: "text-content-secondary hover:bg-surface-subtle hover:text-content",
        link: "text-brand-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[var(--control-h)] px-4 py-2",
        sm: "h-[var(--control-h-sm)] px-3 text-xs",
        lg: "h-[var(--control-h-lg)] px-8",
        dense: "h-[var(--control-h-dense)] px-3 text-xs rounded-md",
        icon: "h-[var(--control-h-sm)] w-[var(--control-h-sm)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
